/**
 * @fileoverview SuggestedQuestionsCarousel — horizontal carousel of
 * follow-up question cards rendered beneath an assistant message. Each
 * card has a leading `citation` icon + the question text. Left/right
 * chevron buttons advance the active card index (owner-controlled).
 *
 * Foundry mapping: `aip-assist-impl__suggested-questions-container` with
 * `__nav-button` (left/right) and a `__carousel` of `__question-card`
 * entries; the focused one has the `__active` modifier.
 */

import type { ReactElement } from "react";
import { Button, Icon } from "@blueprintjs/core";

interface Props {
  questions: readonly string[];
  activeIndex: number;
  onPrev: () => void;
  onNext: () => void;
  onPick?: (index: number) => void;
  className?: string;
}

export function SuggestedQuestionsCarousel({
  questions,
  activeIndex,
  onPrev,
  onNext,
  onPick,
  className,
}: Props): ReactElement {
  return (
    <div data-testid="suggested-questions-carousel"
      className={["bp-suggested-questions", className].filter(Boolean).join(" ")}
      tabIndex={0}
    >
      <Button
        data-testid="suggested-questions-carousel-prev"
        aria-label="Previous question"
        icon="chevron-left"
        className="bp-suggested-questions-nav bp-suggested-questions-nav--left"
        onClick={onPrev}
        disabled={activeIndex === 0}
      />
      <div className="bp-suggested-questions-carousel">
        {questions.map((q, i) => (
          <div
            key={i}
            data-testid={`suggested-questions-carousel-card-${i}`}
            className={[
              "bp-suggested-questions-card",
              i === activeIndex && "bp-suggested-questions-card--active",
            ].filter(Boolean).join(" ")}
            role={onPick ? "button" : undefined}
            tabIndex={onPick ? 0 : -1}
            onClick={onPick ? () => onPick(i) : undefined}
          >
            <Icon icon="citation" size={16} className="bp-suggested-questions-icon" />
            {q}
          </div>
        ))}
      </div>
      <Button
        data-testid="suggested-questions-carousel-next"
        aria-label="Next question"
        icon="chevron-right"
        className="bp-suggested-questions-nav bp-suggested-questions-nav--right"
        onClick={onNext}
        disabled={activeIndex >= questions.length - 1}
      />
    </div>
  );
}
